// src/components/sidebar/components/Content.js
// ENHANCED: Modern navigation with smooth animations, gradient accents, improved UX
// Features: Staggered animations, active indicators, hover effects, enhanced visual hierarchy
//
// --- UPDATE (SLA AI + smart sidebar) --------------------------------------------
//   • SLA Command Center link (routes.js) with a live badge: jobs past SLA (red) or at risk
//     (orange). Push Notifications shows your unread FlowSense Notify count.
//   • FlowSense AI "Ops Pulse" card: one plain-English line on what needs attention first
//     (AI reassignments waiting for approval → jobs past SLA → acknowledgements → jobs at
//     risk → compliance below target), with breached / at-risk / approval counters and the
//     30-day SLA compliance bar. Supervisors see the SLA view; everyone else their notifications.
//     Live: refreshes on SLA AI websocket events (debounced) + every 90 s, pauses in hidden tabs,
//     backs off on errors, and hides itself quietly when the SLA AI API is not installed.
//   • Brand shows the same status (green / amber / red) on the logo.
//   • Search: press "/" or Ctrl/⌘+K, type ("breach", "pothole", "stock"…), ↑/↓, Enter.
//     Matches names, keywords and sections, best match first.
//   • "Quick access": your most-used pages, ranked by frequency with a 7-day decay
//     (kept in this browser only).
//   • Grouped, collapsible sections (remembered per browser); the section holding the
//     current page always stays open.
//   • Role-aware: links a user cannot open are hidden (route.role, incl. '@groups').
//   • Fixes: icons were re-mounted every render (`as={() => route.icon}`); AnimatePresence
//     mode="wait" on a list; "/Issue Management" never highlighted (URL-encoded space);
//     "/admin/workorders" also matched other paths containing it; unused imports; reduced-motion.
//   • Optional props: `user` (else read from storage / the JWT) and `onNavigate` (call to close
//     the mobile drawer after a link is clicked).
// --- END UPDATE -----------------------------------------------------------------

import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import {
  Box,
  Flex,
  Text,
  Link as ChakraLink,
  VStack,
  HStack,
  Icon,
  IconButton,
  Divider,
  useColorModeValue,
  Badge,
  Tooltip,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Kbd,
  Collapse,
  Skeleton,
  Button,
} from '@chakra-ui/react';
import {
  MdSearch,
  MdClose,
  MdExpandMore,
  MdAutoAwesome,
  MdChevronRight,
  MdHistory,
  MdRefresh,
} from 'react-icons/md';
import PropTypes from 'prop-types';
import SidebarBrand from './Brand';
// --- UPDATE: shared FlowSense modules (Notify + RBAC and SLA AI packages)
import { ROUTE_SECTIONS, canAccessRoute } from '../../../routes';
import { hasRole, roleMeta } from '../../../config/roles';
import { useNotify } from '../../notify/NotifyProvider';
import { slaAiApi } from '../../../services/slaAiApi';
import useFlowSenseLive from '../../../hooks/useFlowSenseLive';
import { getAuthToken, getUserInfo } from '../../../utils/auth';
// --- END UPDATE

// ============================================================================
// MOTION COMPONENTS
// ============================================================================
const MotionBox = motion(Box);

// ============================================================================
// ANIMATION VARIANTS
// ============================================================================
const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.25 } },
};

// Items animate on their own (with a per-index delay): sections sit inside Chakra Collapse,
// which would otherwise stop the parent's stagger from reaching them.
const itemVariants = {
  hidden: { opacity: 0, x: -20 },
  visible: (i = 0) => ({
    opacity: 1,
    x: 0,
    transition: { type: "spring", stiffness: 300, damping: 25, delay: Math.min(i, 20) * 0.03 }
  }),
};

const activeIndicatorVariants = {
  hidden: { scaleY: 0, opacity: 0 },
  visible: {
    scaleY: 1,
    opacity: 1,
    transition: { type: "spring", stiffness: 300, damping: 25 }
  }
};

// ============================================================================
// --- UPDATE: helpers (pure — exported for tests)
// ============================================================================
const SLA_POLL_MS = 90 * 1000;
const SLA_MAX_BACKOFF_MS = 10 * 60 * 1000;
const VISITS_KEY = 'fs.sidebar.visits.v1';
const COLLAPSED_KEY = 'fs.sidebar.collapsed.v1';
const VISIT_HALF_LIFE_MS = 7 * 24 * 3600 * 1000;
const COMPLIANCE_TARGET = 85;
const USER_KEYS = ['user', 'userInfo', 'currentUser', 'authUser', 'flowsense_user'];
const SECTION_LABEL = Object.fromEntries((ROUTE_SECTIONS || []).map(s => [s.key, s.label]));

const storage = {
  get(key, fallback) {
    try { const raw = window.localStorage.getItem(key); return raw ? JSON.parse(raw) : fallback; } catch (_) { return fallback; }
  },
  set(key, value) {
    try { window.localStorage.setItem(key, JSON.stringify(value)); } catch (_) { /* private mode / quota */ }
  },
};

const safeDecode = (s) => { try { return decodeURIComponent(s); } catch (_) { return s; } };
export const routeKey = (route) => `${route.layout || ''}${route.path || ''}`;

/** Current user: explicit prop → getUserInfo() (sessionStorage) → common storage keys → JWT payload. */
export function readStoredUser() {
  try {
    const info = getUserInfo?.();
    if (info && (info.role || info.id)) return info;
  } catch (_) { /* fall through */ }
  for (const k of USER_KEYS) {
    const v = storage.get(k, null);
    const u = v && (v.user || v.data?.user || v);
    if (u && typeof u === 'object' && (u.role || u.id)) return u;
  }
  try {
    const token = getAuthToken?.();
    const part = token && String(token).split('.')[1];
    if (part) {
      const json = JSON.parse(atob(part.replace(/-/g, '+').replace(/_/g, '/')));
      return json.user || json;
    }
  } catch (_) { /* not a JWT */ }
  return null;
}

const plural = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;
const fmtLate = (min) => {
  const m = Math.max(0, Math.round(Number(min) || 0));
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  return h < 48 ? `${h} h ${m % 60 ? `${m % 60} min` : ''}`.trim() : `${Math.round(h / 24)} days`;
};

/**
 * FlowSense AI ops insight: the ONE thing to look at first.
 * @returns {{level:'ok'|'warn'|'critical'|'idle', title:string, text:string, action:{kind:'route'|'notify'|null, path?:string}, label:string}}
 */
export function buildOpsInsight({ sla, notify, canSeeSla }) {
  const live = sla?.live || {};
  const breached = Number(live.breached) || 0;
  const atRisk = Number(live.at_risk) || 0;
  const pending = Number(sla?.pending_approval) || 0;
  const compliance = sla?.last_30_days?.compliance_rate;
  const ack = Number(notify?.ack_pending) || 0;
  const critical = Number(notify?.unread_critical) || 0;
  const unread = Number(notify?.unread) || 0;
  const worst = (sla?.hot || []).find(j => Number(j.minutes_late) > 0);
  const slaRoute = { kind: 'route', path: '/admin/sla-ai' };
  const inbox = { kind: 'notify' };

  if (canSeeSla && pending) {
    return { level: 'critical', label: 'Approval needed', title: `${plural(pending, 'AI reassignment')} waiting`, text: `SLA AI wants to move ${pending === 1 ? 'a late job' : `${pending} late jobs`} to another crew and needs your OK.`, action: slaRoute };
  }
  if (canSeeSla && breached) {
    return {
      level: 'critical', label: `${breached} past SLA`, title: `${plural(breached, 'job')} past SLA`,
      text: worst ? `Worst: ${worst.title || `WO #${worst.id}`}${worst.assignee ? ` (${worst.assignee})` : ''} — ${fmtLate(worst.minutes_late)} late.` : 'Escalated to supervisors. Open the command center to act.',
      action: slaRoute,
    };
  }
  if (ack) {
    return { level: 'critical', label: 'Acknowledge', title: `${plural(ack, 'notification')} to acknowledge`, text: 'Unacknowledged critical notifications escalate up the chain automatically.', action: inbox };
  }
  if (critical) {
    return { level: 'warn', label: 'Critical unread', title: `${plural(critical, 'critical notification')} unread`, text: 'Open the notification centre to review.', action: inbox };
  }
  if (canSeeSla && atRisk) {
    return { level: 'warn', label: `${atRisk} at risk`, title: `${plural(atRisk, 'job')} at risk`, text: 'Over 80 % of the SLA window used. Act now to avoid a breach and a reassignment.', action: slaRoute };
  }
  if (canSeeSla && compliance != null && compliance < COMPLIANCE_TARGET) {
    return { level: 'warn', label: `${compliance}% compliance`, title: 'SLA compliance below target', text: `${compliance}% of graded jobs met SLA in the last 30 days (target ${COMPLIANCE_TARGET}%). See the scoreboard for who needs support.`, action: slaRoute };
  }
  if (!sla && !notify) return { level: 'idle', label: 'Smart Monitoring', title: 'FlowSense AI', text: 'Connecting…', action: { kind: null } };
  if (canSeeSla && sla) {
    const active = Number(live.active) || 0;
    return {
      level: 'ok', label: 'AI · all clear', title: active ? `All ${plural(active, 'active job')} on track` : 'No SLA jobs open',
      text: compliance != null ? `${compliance}% SLA compliance over 30 days.${unread ? ` ${plural(unread, 'unread notification')}.` : ''}` : (unread ? `${plural(unread, 'unread notification')}.` : 'Nothing needs you right now.'),
      action: slaRoute,
    };
  }
  return { level: 'ok', label: 'AI · all clear', title: unread ? `${plural(unread, 'unread notification')}` : "You're all caught up", text: unread ? 'Nothing critical.' : 'Nothing needs you right now.', action: unread ? inbox : { kind: null } };
}

/** Search relevance (0 = no match). */
export function scoreRoute(route, q) {
  const query = q.trim().toLowerCase();
  if (!query) return 0;
  const name = String(route.name || '').toLowerCase();
  if (name === query) return 120;
  if (name.startsWith(query)) return 100;
  if (name.split(/\s+/).some(w => w.startsWith(query))) return 85;
  if (name.includes(query)) return 70;
  const kws = (route.keywords || []).map(k => String(k).toLowerCase());
  if (kws.some(k => k.startsWith(query))) return 60;
  if (kws.some(k => k.includes(query))) return 45;
  if (String(SECTION_LABEL[route.section] || '').toLowerCase().includes(query)) return 25;
  // typo-tolerant subsequence ("wrkord" → Work Orders)
  let i = 0;
  for (const ch of name) if (ch === query[i]) i++;
  return i === query.length && query.length >= 3 ? 10 : 0;
}

/** Visit counter with exponential decay → "Quick access". */
export function bumpVisit(visits, key, now = Date.now()) {
  const v = visits[key];
  const decayed = v ? v.s * Math.pow(0.5, (now - v.t) / VISIT_HALF_LIFE_MS) : 0;
  const next = { ...visits, [key]: { s: decayed + 1, t: now } };
  const keys = Object.keys(next);
  if (keys.length > 40) {
    keys.sort((a, b) => next[a].s - next[b].s).slice(0, keys.length - 40).forEach(k => delete next[k]);
  }
  return next;
}

export function rankQuickAccess(routes, visits, currentKey, now = Date.now(), limit = 3) {
  return routes
    .map(r => {
      const v = visits[routeKey(r)];
      return { r, s: v ? v.s * Math.pow(0.5, (now - v.t) / VISIT_HALF_LIFE_MS) : 0 };
    })
    .filter(x => x.s >= 1.5 && routeKey(x.r) !== currentKey)
    .sort((a, b) => b.s - a.s)
    .slice(0, limit)
    .map(x => x.r);
}
// --- END UPDATE

// ============================================================================
// --- UPDATE: SLA AI overview for the pulse card + badges
// ============================================================================
function useSlaPulse(enabled) {
  const [state, setState] = useState({ data: null, loading: enabled, error: null, unavailable: false, loadedAt: null });
  const isMounted = useRef(true);
  const ctrl = useRef(null);
  const timer = useRef(null);
  const failures = useRef(0);
  const unavailable = useRef(false);
  const lastLoad = useRef(0);
  const loadRef = useRef(() => {});

  const schedule = useCallback((ms) => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => loadRef.current(), ms);
  }, []);

  const load = useCallback(async () => {
    if (!enabled || unavailable.current) return;
    if (typeof document !== 'undefined' && document.hidden) { schedule(SLA_POLL_MS); return; }
    ctrl.current?.abort?.();
    const ac = typeof AbortController === 'function' ? new AbortController() : null;
    ctrl.current = ac;
    lastLoad.current = Date.now();
    try {
      const data = await slaAiApi.overview(ac?.signal);
      if (!isMounted.current || ctrl.current !== ac) return;
      failures.current = 0;
      setState({ data, loading: false, error: null, unavailable: false, loadedAt: Date.now() });
      schedule(SLA_POLL_MS);
    } catch (e) {
      if (e?.name === 'AbortError' || !isMounted.current || ctrl.current !== ac) return;
      if (e?.status === 403 || e?.status === 404) {
        // not allowed, or the SLA AI backend is not installed → hide the SLA part quietly
        unavailable.current = true;
        setState({ data: null, loading: false, error: null, unavailable: true, loadedAt: null });
        return;
      }
      failures.current += 1;
      setState(s => ({ ...s, loading: false, error: e?.message || 'SLA AI unavailable' }));
      schedule(Math.min(SLA_MAX_BACKOFF_MS, SLA_POLL_MS * 2 ** Math.min(failures.current, 3)));
    }
  }, [enabled, schedule]);

  loadRef.current = load;

  useEffect(() => {
    isMounted.current = true;
    if (enabled) load();
    const onVisible = () => {
      if (!document.hidden && Date.now() - lastLoad.current > 30000) loadRef.current();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      isMounted.current = false;
      document.removeEventListener('visibilitychange', onVisible);
      clearTimeout(timer.current);
      ctrl.current?.abort?.();
    };
  }, [enabled, load]);

  // SLA AI pushes decisions / warnings / grades over the websocket → refresh (debounced)
  useFlowSenseLive(['sla'], () => loadRef.current(), { debounceMs: 4000, enabled: enabled && !state.unavailable });

  const retry = useCallback(() => {
    failures.current = 0;
    setState(s => ({ ...s, loading: s.data == null, error: null }));
    loadRef.current();
  }, []);

  return useMemo(() => ({ ...state, retry }), [state, retry]);
}
// --- END UPDATE

// ============================================================================
// --- UPDATE: one nav link (memoised; colours come from the parent — hooks stay hoisted)
// ============================================================================
const NavItem = memo(function NavItem({ route, index, isActive, isSelected, badge, theme, onNavigate, registerRef, reduceMotion, sectionHint }) {
  const key = routeKey(route);
  const setRef = useCallback((el) => registerRef(key, el), [registerRef, key]);
  return (
    <MotionBox
      variants={reduceMotion ? undefined : itemVariants}
      custom={index}
      initial={reduceMotion ? false : 'hidden'}
      animate="visible"
      whileHover={reduceMotion ? undefined : { x: 4 }}
      whileTap={reduceMotion ? undefined : { scale: 0.98 }}
    >
      <ChakraLink
        as={RouterLink}
        to={key}
        ref={setRef}
        onClick={onNavigate}
        style={{ textDecoration: 'none' }}
        _focusVisible={{ outline: '2px solid', outlineColor: theme.activeBorder, outlineOffset: '2px', borderRadius: '12px' }}
        _focus={{ boxShadow: 'none' }}
        display="block"
        w="100%"
        aria-current={isActive ? 'page' : undefined}
      >
        <Box position="relative">
          <Flex
            align="center"
            justify="space-between"
            p="10px 14px"
            borderRadius="xl"
            role="group"
            cursor="pointer"
            bg={isActive ? theme.activeBg : isSelected ? theme.linkHoverBg : 'transparent'}
            color={isActive ? theme.activeColor : theme.linkColor}
            fontWeight={isActive ? '700' : '600'}
            borderWidth="1px"
            borderColor={isActive ? theme.activeBorder : isSelected ? theme.linkHoverColor : 'transparent'}
            position="relative"
            overflow="hidden"
            transition="all 0.2s cubic-bezier(0.4, 0, 0.2, 1)"
            _hover={{
              bg: theme.linkHoverBg,
              color: theme.linkHoverColor,
              borderColor: theme.linkHoverColor,
              boxShadow: theme.shadowHover,
              transform: reduceMotion ? undefined : 'translateY(-1px)',
            }}
            _active={{ transform: 'translateY(0px)' }}
          >
            {/* Active indicator - left border */}
            {isActive && (
              <MotionBox
                variants={activeIndicatorVariants}
                initial={reduceMotion ? false : 'hidden'}
                animate="visible"
                position="absolute"
                left={0}
                top={0}
                bottom={0}
                width="4px"
                bgGradient={theme.gradientAccent}
                borderRadius="0 4px 4px 0"
              />
            )}

            <Flex align="center" flex={1} minW={0}>
              {route.icon && (
                <Flex
                  mr="12px"
                  align="center"
                  justify="center"
                  w="20px"
                  h="20px"
                  flexShrink={0}
                  color={isActive ? theme.activeIcon : 'inherit'}
                  transition="all 0.2s cubic-bezier(0.4, 0, 0.2, 1)"
                  _groupHover={{ color: theme.iconHoverColor, transform: reduceMotion ? undefined : 'scale(1.1) rotate(5deg)' }}
                  fontSize="18px"
                >
                  {route.icon}
                </Flex>
              )}
              <Text fontSize="sm" letterSpacing="tight" noOfLines={1}>{route.name}</Text>
              {route.ai && (
                <Tooltip label="Powered by FlowSense AI" hasArrow placement="top" bg={theme.tooltipBg} color={theme.tooltipColor} openDelay={400}>
                  <Flex as="span" ml={1.5} align="center" color={theme.aiColor} flexShrink={0} aria-label="AI">
                    <Icon as={MdAutoAwesome} boxSize="12px" />
                  </Flex>
                </Tooltip>
              )}
            </Flex>

            {sectionHint && (
              <Text fontSize="10px" color={theme.mutedColor} ml={2} flexShrink={0} noOfLines={1}>{sectionHint}</Text>
            )}

            {badge && (
              <Tooltip label={badge.label} hasArrow placement="right" bg={theme.tooltipBg} color={theme.tooltipColor}>
                <Badge
                  ml={2}
                  colorScheme={badge.scheme}
                  variant="solid"
                  borderRadius="full"
                  px={2}
                  fontSize="10px"
                  fontWeight="bold"
                  flexShrink={0}
                  aria-label={badge.label}
                >
                  {badge.count > 99 ? '99+' : badge.count}
                </Badge>
              </Tooltip>
            )}
          </Flex>

          {/* Hover glow effect */}
          <Box
            position="absolute"
            inset={0}
            bgGradient={theme.gradientAccent}
            opacity={0}
            borderRadius="xl"
            transition="opacity 0.2s"
            pointerEvents="none"
            filter="blur(10px)"
            _groupHover={{ opacity: isActive ? 0 : 0.1 }}
          />
        </Box>
      </ChakraLink>
    </MotionBox>
  );
});
// --- END UPDATE

// ============================================================================
// --- UPDATE: FlowSense AI ops pulse card
// ============================================================================
const LEVEL_SCHEME = { critical: 'red', warn: 'orange', ok: 'green', idle: 'blue' };

const PulseStat = ({ label, value, scheme, theme }) => (
  <Box flex="1" textAlign="center" py={1} borderRadius="md" bg={value ? theme.statBg[scheme] : 'transparent'}>
    <Text fontSize="md" fontWeight="800" lineHeight="1.1" color={value ? theme.statColor[scheme] : theme.mutedColor}>{value}</Text>
    <Text fontSize="9px" fontWeight="700" textTransform="uppercase" letterSpacing="wide" color={theme.mutedColor}>{label}</Text>
  </Box>
);

const OpsPulseCard = memo(function OpsPulseCard({ insight, sla, canSeeSla, theme, onOpenNotify, onNavigate, reduceMotion }) {
  const scheme = LEVEL_SCHEME[insight.level] || 'blue';
  const live = sla.data?.live || {};
  const compliance = sla.data?.last_30_days?.compliance_rate;
  const engine = sla.data?.engine;
  const showSla = canSeeSla && !sla.unavailable;
  const clickable = insight.action?.kind === 'route' || insight.action?.kind === 'notify';

  const body = (
    <Box
      role="group"
      borderRadius="xl"
      borderWidth="1px"
      borderColor={theme.pulseBorder[scheme]}
      bg={theme.pulseBg}
      p={3}
      position="relative"
      overflow="hidden"
      transition="all .2s"
      _hover={clickable ? { boxShadow: theme.shadowHover, borderColor: theme.pulseBorderHover[scheme] } : undefined}
    >
      <Box position="absolute" top={0} left={0} right={0} h="3px" bg={`${scheme}.400`} />
      <Flex align="center" justify="space-between" mb={1.5}>
        <HStack spacing={1.5}>
          <Icon as={MdAutoAwesome} color={theme.aiColor} boxSize="14px" />
          <Text fontSize="10px" fontWeight="800" letterSpacing="wider" textTransform="uppercase" color={theme.mutedColor}>FlowSense AI</Text>
        </HStack>
        <HStack spacing={1}>
          {sla.error && showSla && (
            <Tooltip label={`SLA AI: ${sla.error} — retry`} hasArrow bg={theme.tooltipBg} color={theme.tooltipColor}>
              <IconButton
                size="xs" variant="ghost" icon={<MdRefresh />} aria-label="Retry SLA AI"
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); sla.retry(); }}
              />
            </Tooltip>
          )}
          <Box w="7px" h="7px" borderRadius="full" bg={`${scheme}.400`} animation={!reduceMotion && insight.level === 'critical' ? 'fs-status-ping 1.6s cubic-bezier(0,0,.2,1) infinite' : undefined} />
        </HStack>
      </Flex>

      {showSla && sla.loading && !sla.data ? (
        <VStack align="stretch" spacing={2}>
          <Skeleton h="12px" borderRadius="md" />
          <Skeleton h="10px" w="80%" borderRadius="md" />
          <Skeleton h="34px" borderRadius="md" />
        </VStack>
      ) : (
        <>
          <Text fontSize="sm" fontWeight="700" color={theme.activeColor} noOfLines={2}>{insight.title}</Text>
          <Text fontSize="xs" color={theme.mutedColor} mt={0.5} noOfLines={3}>{insight.text}</Text>

          {showSla && sla.data && (
            <>
              <HStack spacing={1} mt={2.5}>
                <PulseStat label="Late" value={Number(live.breached) || 0} scheme="red" theme={theme} />
                <PulseStat label="At risk" value={Number(live.at_risk) || 0} scheme="orange" theme={theme} />
                <PulseStat label="Approve" value={Number(sla.data.pending_approval) || 0} scheme="purple" theme={theme} />
              </HStack>
              {compliance != null && (
                <Tooltip label={`SLA compliance, last 30 days (${sla.data.last_30_days?.graded || 0} graded jobs). Target ${COMPLIANCE_TARGET}%.`} hasArrow bg={theme.tooltipBg} color={theme.tooltipColor}>
                  <Box mt={2.5}>
                    <Flex justify="space-between" fontSize="10px" color={theme.mutedColor} mb={1}>
                      <Text fontWeight="700">SLA compliance · 30 d</Text>
                      <Text fontWeight="800" color={compliance >= COMPLIANCE_TARGET ? theme.statColor.green : theme.statColor.orange}>{compliance}%</Text>
                    </Flex>
                    <Box h="5px" borderRadius="full" bg={theme.trackBg} position="relative" overflow="hidden">
                      <Box position="absolute" left={0} top={0} bottom={0} w={`${Math.max(0, Math.min(100, compliance))}%`} borderRadius="full" bg={compliance >= COMPLIANCE_TARGET ? 'green.400' : compliance >= 70 ? 'orange.400' : 'red.400'} transition="width .6s ease" />
                      <Box position="absolute" top={0} bottom={0} left={`${COMPLIANCE_TARGET}%`} w="2px" bg={theme.markerBg} />
                    </Box>
                  </Box>
                </Tooltip>
              )}
            </>
          )}

          {clickable && (
            <Flex align="center" mt={2} fontSize="xs" fontWeight="700" color={theme.linkHoverColor}>
              <Text>{insight.action.kind === 'notify' ? 'Open notifications' : 'Open SLA Command Center'}</Text>
              <Icon as={MdChevronRight} ml={0.5} transition="transform .2s" _groupHover={{ transform: 'translateX(3px)' }} />
            </Flex>
          )}
          {showSla && engine && (
            <Text fontSize="9px" color={theme.mutedColor} mt={1.5} opacity={0.8}>
              {engine.model_version || 'sla-grade'} · {engine.ml_enabled && !engine.ml_cooling_down ? 'ML engine' : 'Node engine'}{sla.data?.mode ? ` · ${sla.data.mode} mode` : ''}
            </Text>
          )}
        </>
      )}
    </Box>
  );

  if (insight.action?.kind === 'route') {
    return (
      <ChakraLink as={RouterLink} to={insight.action.path} onClick={onNavigate} display="block" style={{ textDecoration: 'none' }} _focus={{ boxShadow: 'none' }} _focusVisible={{ outline: '2px solid', outlineColor: theme.activeBorder, borderRadius: '12px' }} aria-label={`${insight.title}. Open SLA Command Center`}>
        {body}
      </ChakraLink>
    );
  }
  if (insight.action?.kind === 'notify') {
    return (
      <Box as="button" type="button" w="100%" textAlign="left" onClick={onOpenNotify} _focusVisible={{ outline: '2px solid', outlineColor: theme.activeBorder, borderRadius: '12px' }} aria-label={`${insight.title}. Open notifications`}>
        {body}
      </Box>
    );
  }
  return body;
});
// --- END UPDATE

function Content(props) {
  const { routes, user: userProp, onNavigate } = props;
  const location = useLocation();
  const reduceMotion = useReducedMotion();

  // ============================================================================
  // ENHANCED THEME COLORS (all hoisted)
  // ============================================================================
  const linkColor = useColorModeValue('gray.700', 'whiteAlpha.800');
  const linkHoverBg = useColorModeValue('blue.50', 'whiteAlpha.100');
  const linkHoverColor = useColorModeValue('blue.600', 'blue.300');
  const iconHoverColor = useColorModeValue('blue.600', 'blue.300');
  const activeColor = useColorModeValue("gray.800", "white");
  const activeIcon = useColorModeValue("blue.500", "blue.300");
  const activeBg = useColorModeValue("blue.50", "whiteAlpha.100");
  const gradientAccent = useColorModeValue("linear(to-r, blue.500, purple.600)", "linear(to-r, blue.400, purple.500)");
  const activeBorder = useColorModeValue("blue.500", "blue.400");
  const itemBorder = useColorModeValue("gray.200", "whiteAlpha.200");
  const shadowHover = useColorModeValue("0 4px 12px rgba(66, 153, 225, 0.15)", "0 4px 12px rgba(99, 179, 237, 0.2)");
  const navHeaderColor = useColorModeValue("gray.500", "gray.400");
  const scrollbarThumb = useColorModeValue('gray.300', 'whiteAlpha.300');
  const tooltipBg = useColorModeValue("gray.700", "gray.300");
  const tooltipColor = useColorModeValue("white", "gray.800");
  const footerTextColor = useColorModeValue("gray.500", "gray.500");
  // --- UPDATE: new surfaces
  const mutedColor = useColorModeValue('gray.500', 'gray.400');
  const aiColor = useColorModeValue('purple.500', 'purple.300');
  const searchBg = useColorModeValue('gray.50', 'whiteAlpha.50');
  const searchBorder = useColorModeValue('gray.200', 'whiteAlpha.200');
  const searchFocus = useColorModeValue('blue.400', 'blue.300');
  const pulseBg = useColorModeValue('white', 'whiteAlpha.50');
  const trackBg = useColorModeValue('gray.100', 'whiteAlpha.200');
  const markerBg = useColorModeValue('gray.500', 'whiteAlpha.700');
  const chipBg = useColorModeValue('gray.100', 'whiteAlpha.100');
  const chipHoverBg = useColorModeValue('blue.100', 'whiteAlpha.200');
  const pulseBorderRed = useColorModeValue('red.200', 'red.700');
  const pulseBorderOrange = useColorModeValue('orange.200', 'orange.700');
  const pulseBorderGreen = useColorModeValue('green.200', 'green.800');
  const pulseBorderBlue = useColorModeValue('blue.200', 'blue.800');
  const pulseBorderRedHover = useColorModeValue('red.400', 'red.500');
  const pulseBorderOrangeHover = useColorModeValue('orange.400', 'orange.500');
  const pulseBorderGreenHover = useColorModeValue('green.400', 'green.500');
  const pulseBorderBlueHover = useColorModeValue('blue.400', 'blue.500');
  const statBgRed = useColorModeValue('red.50', 'rgba(245,101,101,0.12)');
  const statBgOrange = useColorModeValue('orange.50', 'rgba(237,137,54,0.12)');
  const statBgPurple = useColorModeValue('purple.50', 'rgba(159,122,234,0.14)');
  const statColorRed = useColorModeValue('red.600', 'red.300');
  const statColorOrange = useColorModeValue('orange.600', 'orange.300');
  const statColorPurple = useColorModeValue('purple.600', 'purple.300');
  const statColorGreen = useColorModeValue('green.600', 'green.300');

  const theme = useMemo(() => ({
    linkColor, linkHoverBg, linkHoverColor, iconHoverColor, activeColor, activeIcon, activeBg, gradientAccent,
    activeBorder, shadowHover, tooltipBg, tooltipColor, mutedColor, aiColor, pulseBg, trackBg, markerBg,
    pulseBorder: { red: pulseBorderRed, orange: pulseBorderOrange, green: pulseBorderGreen, blue: pulseBorderBlue },
    pulseBorderHover: { red: pulseBorderRedHover, orange: pulseBorderOrangeHover, green: pulseBorderGreenHover, blue: pulseBorderBlueHover },
    statBg: { red: statBgRed, orange: statBgOrange, purple: statBgPurple },
    statColor: { red: statColorRed, orange: statColorOrange, purple: statColorPurple, green: statColorGreen },
  }), [linkColor, linkHoverBg, linkHoverColor, iconHoverColor, activeColor, activeIcon, activeBg, gradientAccent, activeBorder, shadowHover,
    tooltipBg, tooltipColor, mutedColor, aiColor, pulseBg, trackBg, markerBg, pulseBorderRed, pulseBorderOrange, pulseBorderGreen, pulseBorderBlue,
    pulseBorderRedHover, pulseBorderOrangeHover, pulseBorderGreenHover, pulseBorderBlueHover, statBgRed, statBgOrange, statBgPurple,
    statColorRed, statColorOrange, statColorPurple, statColorGreen]);

  // ============================================================================
  // --- UPDATE: who is signed in → which links + which pulse
  // ============================================================================
  const [storedUser, setStoredUser] = useState(() => (userProp ? null : readStoredUser()));
  useEffect(() => {
    if (userProp) return undefined;
    const onStorage = () => setStoredUser(readStoredUser());
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [userProp]);
  const user = userProp || storedUser;
  const canSeeSla = hasRole(user, '@supervision');
  const role = roleMeta(user?.role);

  const { summary: notifySummary, status: notifyStatus, openCenter } = useNotify();
  const sla = useSlaPulse(canSeeSla);

  const insight = useMemo(
    () => buildOpsInsight({ sla: canSeeSla && !sla.unavailable ? sla.data : null, notify: notifySummary, canSeeSla: canSeeSla && !sla.unavailable }),
    [sla.data, sla.unavailable, notifySummary, canSeeSla],
  );
  const brandStatus = useMemo(() => ({ level: insight.level, label: insight.label, detail: insight.title }), [insight]);

  const badges = useMemo(() => {
    const out = {};
    const live = sla.data?.live;
    if (live) {
      const pending = Number(sla.data.pending_approval) || 0;
      if (live.breached) out.sla = { count: live.breached, scheme: 'red', label: `${plural(live.breached, 'job')} past SLA${pending ? ` · ${pending} awaiting approval` : ''}` };
      else if (pending) out.sla = { count: pending, scheme: 'purple', label: `${plural(pending, 'AI reassignment')} awaiting approval` };
      else if (live.at_risk) out.sla = { count: live.at_risk, scheme: 'orange', label: `${plural(live.at_risk, 'job')} at risk of missing SLA` };
    }
    const unread = Number(notifySummary?.unread) || 0;
    if (unread) {
      const urgent = (Number(notifySummary.unread_critical) || 0) + (Number(notifySummary.ack_pending) || 0);
      out.notify = { count: unread, scheme: urgent ? 'red' : 'blue', label: `${plural(unread, 'unread notification')}${urgent ? ` · ${urgent} urgent` : ''}` };
    }
    return out;
  }, [sla.data, notifySummary]);

  // ============================================================================
  // HELPERS
  // ============================================================================
  const pathname = safeDecode(location.pathname || '');
  const isActiveRoute = useCallback((route) => {
    const full = safeDecode(routeKey(route));
    return pathname === full || pathname.startsWith(`${full}/`);
  }, [pathname]);

  // Sidebar routes: admin layout, not explicitly hidden, allowed for this user
  const sidebarRoutes = useMemo(
    () => (routes || []).filter(r => r && r.layout === '/admin' && r.displayInSidebar !== false && canAccessRoute(r, user)),
    [routes, user],
  );
  const activeRoute = useMemo(() => sidebarRoutes.find(isActiveRoute) || null, [sidebarRoutes, isActiveRoute]);
  const activeKey = activeRoute ? routeKey(activeRoute) : null;

  // Visits → quick access
  const [visits, setVisits] = useState(() => storage.get(VISITS_KEY, {}));
  useEffect(() => {
    if (!activeKey) return;
    setVisits(prev => {
      const next = bumpVisit(prev, activeKey);
      storage.set(VISITS_KEY, next);
      return next;
    });
  }, [activeKey]);
  const quickAccess = useMemo(() => rankQuickAccess(sidebarRoutes, visits, activeKey), [sidebarRoutes, visits, activeKey]);

  // Sections
  const [collapsed, setCollapsed] = useState(() => storage.get(COLLAPSED_KEY, {}));
  const toggleSection = useCallback((key) => {
    setCollapsed(prev => {
      const next = { ...prev, [key]: !prev[key] };
      storage.set(COLLAPSED_KEY, next);
      return next;
    });
  }, []);
  const grouped = useMemo(() => {
    const known = new Set((ROUTE_SECTIONS || []).map(s => s.key));
    const groups = (ROUTE_SECTIONS || []).map(s => ({ ...s, routes: sidebarRoutes.filter(r => r.section === s.key) }));
    const other = sidebarRoutes.filter(r => !known.has(r.section));
    if (other.length) groups.push({ key: 'other', label: 'More', routes: other });
    return groups.filter(g => g.routes.length);
  }, [sidebarRoutes]);
  const hasSections = grouped.length > 1;

  // Search
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState(0);
  const searchRef = useRef(null);
  const linkRefs = useRef(new Map());
  const registerRef = useCallback((key, el) => {
    if (el) linkRefs.current.set(key, el); else linkRefs.current.delete(key);
  }, []);
  const results = useMemo(() => {
    if (!query.trim()) return [];
    return sidebarRoutes
      .map(r => ({ r, s: scoreRoute(r, query) }))
      .filter(x => x.s > 0)
      .map(x => ({ ...x, s: x.s + (badges[x.r.badgeKey] ? 1 : 0) })) // live items win ties
      .sort((a, b) => b.s - a.s || String(a.r.name).localeCompare(String(b.r.name)))
      .map(x => x.r);
  }, [sidebarRoutes, query, badges]);
  useEffect(() => { setSelected(0); }, [query]);

  // "/" or Ctrl/⌘+K focuses search (not while typing elsewhere)
  useEffect(() => {
    const onKey = (e) => {
      const t = e.target;
      const typing = t && (t.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(t.tagName));
      const cmdK = (e.metaKey || e.ctrlKey) && String(e.key).toLowerCase() === 'k';
      if ((e.key === '/' && !typing) || cmdK) {
        if (!searchRef.current || searchRef.current.offsetParent === null) return; // sidebar hidden (mobile)
        e.preventDefault();
        searchRef.current.focus();
        searchRef.current.select();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const handleNavigate = useCallback(() => {
    setQuery('');
    if (typeof onNavigate === 'function') onNavigate();
  }, [onNavigate]);

  const onSearchKeyDown = useCallback((e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setSelected(i => Math.min(results.length - 1, i + 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setSelected(i => Math.max(0, i - 1)); }
    else if (e.key === 'Enter') {
      const r = results[selected];
      if (r) { e.preventDefault(); linkRefs.current.get(routeKey(r))?.click(); searchRef.current?.blur(); }
    } else if (e.key === 'Escape') { setQuery(''); searchRef.current?.blur(); }
  }, [results, selected]);

  const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform || '');
  const liveLabel = { live: 'Live', connecting: 'Connecting…', reconnecting: 'Reconnecting…', polling: 'Live (polling)', offline: 'Offline', idle: 'Idle' }[notifyStatus] || 'Idle';
  const liveDot = notifyStatus === 'live' || notifyStatus === 'polling' ? 'green.400' : notifyStatus === 'offline' ? 'red.400' : notifyStatus === 'idle' ? 'gray.400' : 'orange.400';

  const renderItem = (route, { hint, index = 0 } = {}) => {
    const key = routeKey(route);
    return (
      <NavItem
        key={key}
        route={route}
        index={index}
        isActive={key === activeKey}
        isSelected={Boolean(query) && results[selected] === route}
        badge={route.badgeKey ? badges[route.badgeKey] : null}
        theme={theme}
        onNavigate={handleNavigate}
        registerRef={registerRef}
        reduceMotion={reduceMotion}
        sectionHint={hint}
      />
    );
  };
  // --- END UPDATE

  return (
    <Flex direction="column" height="100%" position="relative" zIndex={1}>
      {/* Brand/Logo Section */}
      <SidebarBrand status={brandStatus} />

      {/* --- UPDATE: search ---------------------------------------------------- */}
      <Box px="16px" mb="12px">
        <InputGroup size="sm">
          <InputLeftElement pointerEvents="none" h="36px"><Icon as={MdSearch} color={mutedColor} boxSize="18px" /></InputLeftElement>
          <Input
            ref={searchRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onSearchKeyDown}
            placeholder="Search pages…"
            aria-label="Search pages"
            role="combobox"
            aria-expanded={Boolean(query)}
            aria-controls="fs-sidebar-nav"
            h="36px"
            borderRadius="xl"
            bg={searchBg}
            borderColor={searchBorder}
            fontSize="sm"
            _focusVisible={{ borderColor: searchFocus, boxShadow: `0 0 0 1px var(--chakra-colors-${String(searchFocus).replace('.', '-')})` }}
          />
          <InputRightElement h="36px" w="auto" pr={2}>
            {query ? (
              <IconButton size="xs" variant="ghost" icon={<MdClose />} aria-label="Clear search" onClick={() => { setQuery(''); searchRef.current?.focus(); }} />
            ) : (
              <HStack spacing={0.5} display={{ base: 'none', md: 'flex' }} opacity={0.7}>
                <Kbd fontSize="10px">{isMac ? '⌘' : 'Ctrl'}</Kbd><Kbd fontSize="10px">K</Kbd>
              </HStack>
            )}
          </InputRightElement>
        </InputGroup>
      </Box>

      {/* --- UPDATE: FlowSense AI ops pulse ------------------------------------- */}
      {!query && (
        <Box px="16px" mb="14px">
          <OpsPulseCard
            insight={insight}
            sla={sla}
            canSeeSla={canSeeSla}
            theme={theme}
            onOpenNotify={openCenter}
            onNavigate={handleNavigate}
            reduceMotion={reduceMotion}
          />
        </Box>
      )}
      {/* --- END UPDATE */}

      {/* Links Section with Staggered Animation */}
      <MotionBox
        id="fs-sidebar-nav"
        as="nav"
        aria-label="Main navigation"
        variants={reduceMotion ? undefined : containerVariants}
        initial={reduceMotion ? false : "hidden"}
        animate="visible"
        px="12px"
        flexGrow={1}
        overflowY="auto"
        css={{
          '&::-webkit-scrollbar': { width: '4px' },
          '&::-webkit-scrollbar-track': { background: 'transparent' },
          '&::-webkit-scrollbar-thumb': { background: scrollbarThumb, borderRadius: '24px' },
        }}
      >
        {query ? (
          // --- UPDATE: search results ------------------------------------------
          <VStack spacing={1} align="stretch" pb={2}>
            <Text px="8px" mb={1} fontSize="xs" fontWeight="700" color={navHeaderColor} textTransform="uppercase" letterSpacing="wider">
              {results.length ? `${plural(results.length, 'match', 'matches')}` : 'No matches'}
            </Text>
            {results.length ? results.map((r, i) => renderItem(r, { hint: SECTION_LABEL[r.section], index: i })) : (
              <Box px="8px" py={4} textAlign="center">
                <Text fontSize="sm" color={mutedColor}>No page matches “{query}”.</Text>
                <Button size="xs" variant="ghost" mt={2} onClick={() => setQuery('')}>Clear search</Button>
              </Box>
            )}
          </VStack>
        ) : (
          <VStack spacing={1} align="stretch" pb={2}>
            {/* --- UPDATE: quick access ------------------------------------------- */}
            {quickAccess.length > 0 && (
              <Box mb={2} px="2px" role="group" aria-label="Quick access">
                <HStack spacing={1} px="6px" mb={1.5} color={navHeaderColor}>
                  <Icon as={MdHistory} boxSize="13px" />
                  <Text fontSize="xs" fontWeight="700" textTransform="uppercase" letterSpacing="wider">Quick access</Text>
                </HStack>
                <Flex gap={1.5} flexWrap="wrap" px="4px">
                  {quickAccess.map(r => (
                    <ChakraLink
                      key={routeKey(r)}
                      as={RouterLink}
                      to={routeKey(r)}
                      onClick={handleNavigate}
                      display="inline-flex"
                      alignItems="center"
                      gap={1}
                      px={2.5}
                      py={1}
                      borderRadius="full"
                      bg={chipBg}
                      fontSize="xs"
                      fontWeight="600"
                      color={linkColor}
                      style={{ textDecoration: 'none' }}
                      _hover={{ bg: chipHoverBg, color: linkHoverColor }}
                      _focusVisible={{ outline: '2px solid', outlineColor: activeBorder }}
                      maxW="100%"
                    >
                      <Text as="span" noOfLines={1}>{r.name}</Text>
                      {r.badgeKey && badges[r.badgeKey] && <Box as="span" w="6px" h="6px" borderRadius="full" bg={`${badges[r.badgeKey].scheme}.400`} flexShrink={0} />}
                    </ChakraLink>
                  ))}
                </Flex>
              </Box>
            )}

            {/* Navigation sections */}
            {grouped.map(group => {
              const holdsActive = group.routes.some(r => routeKey(r) === activeKey);
              const isOpen = !hasSections || holdsActive || !collapsed[group.key];
              const groupBadge = group.routes.map(r => r.badgeKey && badges[r.badgeKey]).find(Boolean);
              return (
                <Box key={group.key} mb={1}>
                  {hasSections ? (
                    <Flex
                      as="button"
                      type="button"
                      w="100%"
                      align="center"
                      justify="space-between"
                      px="8px"
                      py="6px"
                      mt={1}
                      borderRadius="md"
                      color={navHeaderColor}
                      onClick={() => toggleSection(group.key)}
                      aria-expanded={isOpen}
                      aria-controls={`fs-sec-${group.key}`}
                      _hover={{ color: linkHoverColor }}
                      _focusVisible={{ outline: '2px solid', outlineColor: activeBorder }}
                    >
                      <HStack spacing={1.5}>
                        <Text fontSize="xs" fontWeight="700" textTransform="uppercase" letterSpacing="wider">{group.label}</Text>
                        {!isOpen && groupBadge && <Box w="6px" h="6px" borderRadius="full" bg={`${groupBadge.scheme}.400`} />}
                      </HStack>
                      <Icon as={MdExpandMore} boxSize="16px" transition="transform .2s" transform={isOpen ? 'rotate(0deg)' : 'rotate(-90deg)'} opacity={holdsActive ? 0.35 : 1} />
                    </Flex>
                  ) : (
                    <Box px="8px" mb="8px">
                      <Text fontSize="xs" fontWeight="700" color={navHeaderColor} textTransform="uppercase" letterSpacing="wider">Navigation</Text>
                    </Box>
                  )}
                  <Collapse in={isOpen} animateOpacity unmountOnExit={false}>
                    <VStack id={`fs-sec-${group.key}`} spacing={1} align="stretch">
                      {group.routes.map(r => renderItem(r, { index: sidebarRoutes.indexOf(r) }))}
                    </VStack>
                  </Collapse>
                </Box>
              );
            })}
            {/* --- END UPDATE */}
          </VStack>
        )}
      </MotionBox>

      {/* Footer Section */}
      <Box px="20px" py="14px" mt="auto">
        <Divider mb={3} borderColor={itemBorder} opacity={0.6} />
        {/* --- UPDATE: who + live status */}
        <Flex align="center" justify="space-between" gap={2}>
          <Tooltip label={`Notifications: ${liveLabel}`} hasArrow bg={tooltipBg} color={tooltipColor}>
            <HStack spacing={1.5} minW={0}>
              <Box w="7px" h="7px" borderRadius="full" bg={liveDot} flexShrink={0} />
              <Text fontSize="xs" color={footerTextColor} fontWeight="600" noOfLines={1}>
                {user?.role ? role.short || role.label : liveLabel}
              </Text>
            </HStack>
          </Tooltip>
          <Text fontSize="xs" color={footerTextColor} fontWeight="500" flexShrink={0}>FlowSense v5.1</Text>
        </Flex>
        {/* --- END UPDATE */}
      </Box>
    </Flex>
  );
}

Content.propTypes = {
  routes: PropTypes.arrayOf(PropTypes.object).isRequired,
  user: PropTypes.object, // --- UPDATE (optional)
  onNavigate: PropTypes.func, // --- UPDATE (optional)
};

export default Content;
